import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Checkbox } from '../../../ui/form/Checkbox';
import { Range } from '../../../ui/form/Range';
import { Select } from '../../../ui/form/Select';
import { Pagination } from '../../../ui/pagination/Pagination';
import { SearchInput } from '../../../ui/form/SearchInput';

import { SEARCH_ESTATE } from '../../../../../client/__graphql__/cluster/queries/search/estate';
import { ENV_CONFIG } from '../../../../__config__/env';
import { useQuery } from '@apollo/client';
import { client } from '../../../../__graphql__/clientCluster';
import {
  AssignmentType,
  FacilityType,
  SearchAggregation,
  SearchEstateQuery,
  SearchEstateQueryVariables,
  SearchEstateSort
} from '../../../../__types__/generated/cluster';
import { PropertyCard } from '../../../ui/cards/cluster/Property';
import { Heading } from '../../../ui/text/Heading';
import { css } from 'styled-components';
import { CheckboxGroup } from '../fritid/Fritid';
import { useScrollToElement } from '../../../../utils/hooks/useScrollToElement';
import { useDebounce } from '../../../../utils/hooks/useDebounce';
import SearchFilterTag from '../../../ui/search/SearchFilterTag';
import { filterArrayByMatchCount } from '../utils/filterArrayByMatchCount';
import { AreaSelect } from './filter/AreaSelect';
import { AreaFilter } from './filter/AreaFilter';
import { IAreaOption } from './filter/AreaSubFilter';

interface IFilter {
  sold: boolean;
  price: IRange;
  size: IRange;
  estateTypes: string[];
  facilities: string[];
  facilityTypes: FacilityType[];
  counties: string[];
  municipalities: IAreaOption[];
}

interface IRange {
  from?: number;
  to?: number;
}

interface IAggregatedType {
  key: string;
  label?: string;
  doc_count: number;
}

const SearchProperties = React.forwardRef<HTMLDivElement>((props, ref) => {
  const [query, setQuery] = React.useState('');
  const resultRef = React.useRef<HTMLDivElement>(null);
  const { scrollTo } = useScrollToElement();
  const [pageIndex, setPageIndex] = useState(0);
  const [limit, setLimit] = useState(6);
  const [init, setInit] = useState(true);
  const [filter, setFilter] = useState<IFilter>({
    sold: false,
    price: {},
    size: {},
    estateTypes: [],
    facilities: [],
    facilityTypes: [],
    counties: [],
    municipalities: []
  });

  const [propertyTypesAggregated, setPropertyTypesAggregated] = React.useState<
    IAggregatedType[]
  >([]);
  const [facilityTypesAggregated, setFacilityTypesAggregated] = React.useState<
    IAggregatedType[]
  >([]);
  const [countiesAggregated, setCountiesAggregated] = React.useState<
    SearchAggregation[]
  >([]);
  const [municipalitiesAggregated, setMunicipalitiesAggregated] =
    React.useState<SearchAggregation[]>([]);

  const debouncedQuery = useDebounce<typeof query>(query, 300);
  const debouncedPrice = useDebounce<IRange>(
    {
      from:
        filter?.price?.from && filter?.price?.from > 1000000
          ? filter?.price?.from
          : undefined,
      to:
        filter?.price?.to && filter?.price?.to < 25000000
          ? filter?.price?.to
          : undefined
    },
    300
  );
  const debouncedSize = useDebounce<IRange>(
    {
      from:
        filter?.size?.from && filter?.size?.from > 30
          ? filter?.size?.from
          : undefined,
      to:
        filter?.size?.to && filter?.size?.to < 400
          ? filter?.size?.to
          : undefined
    },
    300
  );

  const { data, loading, error } = useQuery<
    SearchEstateQuery,
    SearchEstateQueryVariables
  >(SEARCH_ESTATE, {
    client,
    fetchPolicy: 'no-cache',
    variables: {
      input: { brandId: ENV_CONFIG?.BRAND_ID, query: debouncedQuery },
      filter: {
        aggregations: true,
        isProjectUnit: false,
        published: true,
        sold: filter?.sold ? true : undefined,
        assignmentTypes: [AssignmentType.Leisure],
        pagination: { page: pageIndex, limit: limit },
        estateTypes: filter?.estateTypes,
        facilities: filter?.facilities,
        facilityTypes: filter?.facilityTypes,
        counties: filter?.counties,
        price: debouncedPrice,
        size: debouncedSize,
        sort: filter?.sold
          ? SearchEstateSort.RecentlySold
          : SearchEstateSort.PublishedNewOld,
        municipalities: filter?.municipalities
          ?.filter(
            (e) =>
              e?.name?.length &&
              e.parentKey &&
              filter.counties.includes(e.parentKey)
          )
          ?.map((e) => e?.name)
      }
    },
    onCompleted: ({ search }) => {
      const {
        aggregations = null,
        list = [],
        total = 0
      } = search?.estate ?? {};
      if (aggregations && init) {
        setPropertyTypesAggregated(
          aggregations?.estateTypes
            ?.filter((e) => e?.count)
            ?.map<IAggregatedType>((item) => ({
              key: item?.key ?? ``,
              doc_count: item?.count ?? 0
            })) ?? []
        );
        setFacilityTypesAggregated(
          aggregations?.facilities
            ?.filter((e) => e?.count)
            ?.map<IAggregatedType>((e) => ({
              key: e?.key ?? ``,
              label: e?.label,
              doc_count: e?.count ?? 0
            })) ?? []
        );
        setCountiesAggregated(
          aggregations?.counties?.filter((e) => e?.count) ?? []
        );
        setMunicipalitiesAggregated(
          aggregations?.municipalities?.filter((e) => e?.count) ?? []
        );
        setInit(false);
      }
    }
  });

  const onFacilityTypeChange = (value: FacilityType) => {
    if (!value) return;
    if (filter?.facilityTypes?.includes(value)) {
      setFilter((prevState) => ({
        ...prevState,
        facilityTypes: prevState.facilityTypes.filter((e) => e !== value)
      }));
    } else {
      setFilter((prevState) => ({
        ...prevState,
        facilityTypes: [...prevState.facilityTypes, value]
      }));
    }
  };

  return (
    <Container ref={ref}>
      <Heading center tag="h1">
        Våre fritidseiendommer
      </Heading>
      <CheckboxGroup className="checkgroupbox">
        <Checkbox
          className="fjell"
          checked={filter.facilityTypes.includes(FacilityType.Mountains)}
          labelPos="right"
          label="Fjell"
          onClick={() => {
            onFacilityTypeChange(FacilityType.Mountains);
          }}
        />
        <Checkbox
          className="sjø"
          checked={filter.facilityTypes.includes(FacilityType.Coast)}
          labelPos="right"
          label="Sjø"
          onClick={() => {
            onFacilityTypeChange(FacilityType.Coast);
          }}
        />
        <Checkbox
          checked={filter.sold}
          labelPos="right"
          label="Solgte"
          onClick={() => {
            setFilter((prevState) => ({
              ...prevState,
              sold: !prevState.sold
            }));
          }}
        />
      </CheckboxGroup>
      <FilterWrapper>
        <FilterOption>
          <AreaSelect counties={filter?.counties}>
            <AreaFilter
              counties={countiesAggregated}
              municipalities={municipalitiesAggregated}
              selectedCounties={filter.counties}
              selectedMunicipalities={filter.municipalities}
              onCountyClick={(value) => {
                if (!value) return;
                if (filter?.counties?.includes(value)) {
                  setFilter((prevState) => ({
                    ...prevState,
                    counties: prevState?.counties?.filter((e) => e !== value)
                  }));
                } else {
                  setFilter((prevState) => ({
                    ...prevState,
                    counties: [...prevState?.counties, value]
                  }));
                }
              }}
              onMunicipalityClick={(value, parent) => {
                if (!value) return;
                const idx = filter?.municipalities?.findIndex(
                  (e) => e?.parentKey === parent && e?.name === value
                );
                if (idx > -1) {
                  setFilter((prevState) => ({
                    ...prevState,
                    municipalities: prevState?.municipalities?.filter(
                      (e) => !(e?.name === value && e?.parentKey === parent)
                    )
                  }));
                } else {
                  setFilter((prevState) => ({
                    ...prevState,
                    municipalities: [
                      ...prevState?.municipalities,
                      { parentKey: parent, name: value }
                    ]
                  }));
                }
              }}
            />
          </AreaSelect>
          {filter.counties.length > 0 ? (
            <SearchFilterTagContainer>
              {filter.counties.map((item, index) => (
                <SearchFilterTag
                  key={`tag_counties_${index}`}
                  label={item}
                  onClick={() => {
                    setFilter((prevState) => ({
                      ...prevState,
                      counties: prevState?.counties?.filter((e) => e !== item)
                    }));
                  }}
                />
              ))}
            </SearchFilterTagContainer>
          ) : null}
        </FilterOption>
        <FilterOption>
          <Select
            name="boligtype"
            placeholder="Eiendomstype"
            value={0}
            onChange={(e) => {
              const { value = null } = e?.target;
              if (!value) return;
              if (filter?.estateTypes?.includes(value)) {
                setFilter((prevState) => ({
                  ...prevState,
                  estateTypes: prevState?.estateTypes?.filter(
                    (e) => e !== value
                  )
                }));
              } else {
                setFilter((prevState) => ({
                  ...prevState,
                  estateTypes: [...prevState?.estateTypes, value]
                }));
              }
            }}
          >
            <option disabled value="0">
              Eiendomstype
            </option>
            {filterArrayByMatchCount(propertyTypesAggregated).map(
              (item, index) => (
                <option value={item.key} key={`opt${index}`}>
                  {item.key}
                </option>
              )
            )}
          </Select>
          {filter.estateTypes.length > 0 ? (
            <SearchFilterTagContainer>
              {filter.estateTypes.map((item, index) => (
                <SearchFilterTag
                  key={`tag${index}`}
                  label={item}
                  onClick={() => {
                    setFilter((prevState) => ({
                      ...prevState,
                      estateTypes: prevState?.estateTypes?.filter(
                        (e) => e !== item
                      )
                    }));
                  }}
                />
              ))}
            </SearchFilterTagContainer>
          ) : null}
        </FilterOption>
        <FilterOption>
          <Select
            name="fasiliteter"
            placeholder="Fasiliteter"
            value={0}
            onChange={(e) => {
              const { value = null } = e?.target;
              if (!value) return;
              if (filter?.facilities?.includes(value)) {
                setFilter((prevState) => ({
                  ...prevState,
                  facilities: prevState?.facilities?.filter((e) => e !== value)
                }));
              } else {
                setFilter((prevState) => ({
                  ...prevState,
                  facilities: [...prevState?.facilities, value]
                }));
              }
            }}
          >
            <option disabled value="0">
              Fasiliteter
            </option>
            {facilityTypesAggregated.map((item) => (
              <option value={item?.label ?? item.key} key={item.key}>
                {item?.label ?? item.key}
              </option>
            ))}
          </Select>
          {filter.facilities.length > 0 ? (
            <SearchFilterTagContainer>
              {filter.facilities.map((item) => (
                <SearchFilterTag
                  key={`tag_${item}`}
                  label={
                    facilityTypesAggregated?.find((e) => e?.key === item)
                      ?.label ?? item
                  }
                  onClick={() => {
                    setFilter((prevState) => ({
                      ...prevState,
                      facilities: prevState?.facilities?.filter(
                        (e) => e !== item
                      )
                    }));
                  }}
                />
              ))}
            </SearchFilterTagContainer>
          ) : null}
        </FilterOption>
        <FilterOption>
          <SearchInput
            label="Fritekstsøk"
            placeholder="Område, postnummer og adresse"
            className="fritekstsøklabel"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </FilterOption>
        <FilterOption>
          <Range
            className="rangelabel"
            label="Prisantydning"
            asCurrency
            range={{
              min: 1000000,
              max: 25000000,
              step: 1000,
              onChange: ([from, to]) => {
                setFilter((prevState) => ({
                  ...prevState,
                  price: {
                    from: from,
                    to: to
                  }
                }));
              },
              value: [
                filter?.price?.from ?? 1000000,
                filter?.price?.to ?? 25000000
              ]
            }}
            unit="kr"
          />
        </FilterOption>
        <FilterOption>
          <Range
            className="rangelabel"
            label="Størrelse"
            range={{
              min: 30,
              max: 400,
              step: 10,
              onChange: ([from, to]) => {
                setFilter((prevState) => ({
                  ...prevState,
                  size: {
                    from: from,
                    to: to
                  }
                }));
              },
              value: [filter?.size?.from ?? 30, filter?.size?.to ?? 400]
            }}
            unit="m&sup2;"
          />
        </FilterOption>
      </FilterWrapper>
      {data?.search?.estate?.total ? (
        <>
          <PropertyList ref={resultRef}>
            {data?.search?.estate?.list?.map((item, index) => (
              <PropertyCard
                className="propertycard"
                key={`property-${item?.estateId}-${index}`}
                item={item}
                currentPage={1}
              />
            ))}
          </PropertyList>
          <Pagination
            className="pagination"
            center
            currentPage={pageIndex}
            maxPages={data?.search?.estate?.total / limit}
            total={data?.search?.estate?.total}
            itemsPerPage={limit}
            pageRangeDisplayed={1}
            pageOnChange={({ selected }) => {
              if (typeof scrollTo === 'function') {
                scrollTo({
                  ref: resultRef,
                  distanceTop: 100,
                  behaviour: 'auto'
                });
                setPageIndex(selected);
              }
            }}
          />
        </>
      ) : (
        <NoResults>
          <p>Ingen resultater funnet..</p>
        </NoResults>
      )}
    </Container>
  );
});

export default SearchProperties;

const Container = styled.div`
  .checkgroupbox {
    display: grid;
    grid-template-rows: 1fr;
    grid-column-gap: 1px;
    margin-left: 16px;
    padding: 2em 2em;
  }

  @media all and (min-width: 600px) {
    .pagination {
      margin-top: 2em;
    }
    .checkgroupbox {
      grid-auto-flow: column;
      grid-row-gap: 1em;
    }
    .fritekstsøklabel {
      margin-top: unset;
    }
  }
`;

const NoResults = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 550px;
  padding-top: 200px;
`;

const BaseGrid = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 2em;
  grid-row-gap: 2em;

  @media all and (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media all and (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FilterOption = styled.div`
  align-self: start;
`;

const FilterWrapper = styled.div`
  ${BaseGrid}
  .select-wrapper {
    margin-bottom: 0;
  }
  .align-bottom {
    align-self: start;
  }
`;

const PropertyList = styled.div`
  ${BaseGrid}
  margin-top: 2em;
  min-height: 550px;

  .propertycard {
    text-align: center;
  }
`;

const SearchFilterTagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0.5em 0 1em;
`;
